import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select } from "../components/UI";
import { toast } from "sonner";
import { Tv, Loader2, Plus, X, Play, Target, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat().format(Math.round(n || 0));
const usd = (n) => "$" + fmt(n);

const emptyScenario = (i) => ({
  name: `Scenario ${i}`, districts: [], segment_ids: [], budget: 25000, cpm: 32, weeks: 4, frequency_cap: 6,
});

export default function CTVPlanner() {
  const [scenarios, setScenarios] = useState([emptyScenario(1)]);
  const [segments, setSegments] = useState([]);
  const [districtSearch, setDistrictSearch] = useState("");
  const [districtResults, setDistrictResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/political-data/providers").then(r => setSegments(r.data.filter(s => s.ctv_eligible)));
  }, []);

  useEffect(() => {
    if (!districtSearch) { setDistrictResults([]); return; }
    const t = setTimeout(() => {
      api.get("/ctv-planner/districts", { params: { search: districtSearch } })
        .then(r => setDistrictResults(r.data.districts || []));
    }, 350);
    return () => clearTimeout(t);
  }, [districtSearch]);

  const upd = (idx, patch) => setScenarios(s => s.map((sc, i) => i === idx ? { ...sc, ...patch } : sc));

  const addDistrict = (idx, d) => {
    const sc = scenarios[idx];
    if (!sc.districts.includes(d.district)) upd(idx, { districts: [...sc.districts, d.district] });
    setDistrictSearch(""); setDistrictResults([]);
  };

  const toggleSegment = (idx, sid) => {
    const sc = scenarios[idx];
    upd(idx, { segment_ids: sc.segment_ids.includes(sid) ? sc.segment_ids.filter(x => x !== sid) : [...sc.segment_ids, sid] });
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/ctv-planner/forecast", { scenarios });
      setResults(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forecast failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="CTV · Hero Product"
        title="CTV Voter Universe Planner"
        subtitle="Compare up to 3 flight scenarios across districts and voter segments — with automatic reach-gap detection."
        action={
          <div className="flex gap-2">
            {scenarios.length < 3 && (
              <Btn variant="secondary" onClick={() => { setScenarios([...scenarios, emptyScenario(scenarios.length + 1)]); setActiveIdx(scenarios.length); }} data-testid="add-scenario-btn">
                <Plus className="w-4 h-4" /> Add scenario
              </Btn>
            )}
            <Btn onClick={run} disabled={running} data-testid="run-forecast-btn">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run forecast
            </Btn>
          </div>
        }
      />

      <div className="flex gap-1 border-b border-white/10">
        {scenarios.map((sc, i) => (
          <button key={i} onClick={() => setActiveIdx(i)} data-testid={`scenario-tab-${i}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${activeIdx === i ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"}`}>
            <Tv className="w-4 h-4" /> {sc.name}
            {scenarios.length > 1 && (
              <X className="w-3 h-3 hover:text-rose-300" onClick={(e) => { e.stopPropagation(); setScenarios(scenarios.filter((_, j) => j !== i)); setActiveIdx(0); }} />
            )}
          </button>
        ))}
      </div>

      {scenarios[activeIdx] && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Flight settings</div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Scenario name" value={scenarios[activeIdx].name} onChange={e => upd(activeIdx, { name: e.target.value })} data-testid="scenario-name-input" />
              <Input label="Budget (USD)" type="number" value={scenarios[activeIdx].budget} onChange={e => upd(activeIdx, { budget: +e.target.value })} data-testid="scenario-budget-input" />
              <Input label="CTV CPM (USD)" type="number" value={scenarios[activeIdx].cpm} onChange={e => upd(activeIdx, { cpm: +e.target.value })} />
              <Input label="Flight weeks" type="number" value={scenarios[activeIdx].weeks} onChange={e => upd(activeIdx, { weeks: +e.target.value })} />
              <Input label="Frequency cap / month" type="number" value={scenarios[activeIdx].frequency_cap} onChange={e => upd(activeIdx, { frequency_cap: +e.target.value })} />
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Districts (live district lookup)</label>
              <div className="relative">
                <Input value={districtSearch} onChange={e => setDistrictSearch(e.target.value)} placeholder="Type e.g. GA, NY-1, TX…" data-testid="district-search-input" />
                {districtResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-slate-900 border border-white/15 rounded-md max-h-48 overflow-y-auto" data-testid="district-results">
                    {districtResults.map(d => (
                      <button key={d.district} onClick={() => addDistrict(activeIdx, d)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-cyan-400/10 flex justify-between">
                        <span>{d.district}</span><span className="text-xs text-slate-500">{d.district_type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {scenarios[activeIdx].districts.map(d => (
                  <span key={d} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300">
                    {d} <X className="w-3 h-3 cursor-pointer" onClick={() => upd(activeIdx, { districts: scenarios[activeIdx].districts.filter(x => x !== d) })} />
                  </span>
                ))}
                {scenarios[activeIdx].districts.length === 0 && <span className="text-xs text-slate-600">No districts — defaults to statewide model</span>}
              </div>
            </div>
          </Card>

          <Card>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Voter segments ({scenarios[activeIdx].segment_ids.length} selected)</div>
            <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1" data-testid="planner-segments-list">
              {segments.map(s => (
                <label key={s.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-md border cursor-pointer transition-colors ${scenarios[activeIdx].segment_ids.includes(s.id) ? "border-cyan-400/40 bg-cyan-400/5" : "border-white/10 hover:border-white/20"}`}>
                  <input type="checkbox" checked={scenarios[activeIdx].segment_ids.includes(s.id)} onChange={() => toggleSegment(activeIdx, s.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{s.segment_name}</div>
                    <div className="text-[11px] text-slate-500">{s.provider} · reach {fmt(s.estimated_reach)} · ${s.data_fee_cpm} CPM</div>
                  </div>
                </label>
              ))}
              {segments.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No CTV-eligible segments — sync the catalog from Political Data.</div>}
            </div>
          </Card>
        </div>
      )}

      {results && (
        <Card data-testid="forecast-results">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Scenario comparison</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 uppercase">Modeled estimates</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-4">Metric</th>
                  {results.scenarios.map(s => <th key={s.name} className="py-2 pr-4 text-cyan-300">{s.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Voter universe (HH)", s => fmt(s.universe_households)],
                  ["Budget", s => usd(s.budget)],
                  ["Impressions", s => fmt(s.impressions)],
                  ["Households reached", s => fmt(s.reached_households)],
                  ["Reach %", s => `${s.reach_pct}%`],
                  ["Avg frequency", s => s.avg_frequency],
                  ["Video completions", s => fmt(s.video_completions)],
                  ["Cost / completed view", s => `$${s.cost_per_completed_view}`],
                ].map(([label, f]) => (
                  <tr key={label} className="border-b border-white/5">
                    <td className="py-2 pr-4 text-slate-400">{label}</td>
                    {results.scenarios.map(s => <td key={s.name} className="py-2 pr-4 font-mono text-white">{f(s)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {results.scenarios.map(s => (
              <div key={s.name} className={`rounded-md border p-4 ${s.reach_pct >= 75 ? "border-emerald-400/30 bg-emerald-400/5" : "border-amber-400/30 bg-amber-400/5"}`} data-testid={`reach-gap-${s.name}`}>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold mb-1.5">
                  {s.reach_pct >= 75 ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <AlertTriangle className="w-4 h-4 text-amber-300" />}
                  <span className={s.reach_pct >= 75 ? "text-emerald-300" : "text-amber-300"}>{s.name} — Reach gap</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">{s.reach_gap.message}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-600">{results.model_note}</div>
          <div className="mt-4 flex justify-end">
            <Btn onClick={() => window.location.href = "/campaigns/ctv/new"} data-testid="planner-launch-btn"><Target className="w-4 h-4" /> Launch winning scenario as CTV campaign</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
